import { Eye, FileText } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import type { TopUpRequest } from "@/types/wallet";
import { formatDate, formatINR, formatTime, TOPUP_STATUS_META } from "../data";
import ReceiptPreviewModal from "@/pages/admin/wallets/components/ReceiptPreviewModal";

interface TopupDetailModalProps {
  open: boolean;
  onClose: () => void;
  request: TopUpRequest | null;
}

export default function TopupDetailModal({ open, onClose, request }: TopupDetailModalProps) {
  const [receiptOpen, setReceiptOpen] = useState(false);
  if (!request) return null;
  const status = TOPUP_STATUS_META[request.status];
  const paymentDate = request.requestedAt;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Top-up request details"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-secondary">{request.requestId}</p>

        <span className={`inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${status.bg} ${status.text}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {status.label}
        </span>

        <div className="flex flex-col divide-y divide-line rounded-[10px] border border-line">
          {[
            { label: "Amount", value: formatINR(request.amount) },
            { label: "Payment Mode", value: request.paymentMode },
            { label: "Reference Number", value: request.referenceNumber },
            { label: "Payment Date", value: formatDate(paymentDate) },
            { label: "Requested On", value: `${formatDate(request.requestedAt)} · ${formatTime(request.requestedAt)}` },
            {
              label: "Actioned On",
              value: request.actionedAt ? `${formatDate(request.actionedAt)} · ${formatTime(request.actionedAt)}` : "—",
            },
            { label: "Admin Remark", value: request.adminRemark || "—" },
          ].map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-[13px] text-ink-secondary">{row.label}</span>
              <span className="text-right text-sm font-medium text-ink">{row.value}</span>
            </div>
          ))}
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-ink">Receipt</label>
          <div className="flex items-center gap-3 rounded-[10px] border border-line bg-slate-50/60 p-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-primary-500">
              <FileText className="h-4 w-4" />
            </span>
            <span className="flex-1 truncate text-[13px] text-ink">{request.receiptFileName}</span>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Eye className="h-4 w-4" />}
              onClick={() => setReceiptOpen(true)}
            >
              View Receipt
            </Button>
          </div>
        </div>
      </div>

      <ReceiptPreviewModal
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        request={request}
      />
    </Modal>
  );
}